import { Platform, InteractionManager } from 'react-native';
import { userPreferencesService } from './userPreferencesService';
import { createScopedLogger } from '@/utils/logging/createScopedLogger';

const logger = createScopedLogger('BootScheduler');
const logWithTimestamp = (msg: string, ...args: unknown[]) => logger.info(msg, ...args);
const errorWithTimestamp = (msg: string, ...args: unknown[]) => logger.error(msg, ...args);
import { logoutSignalService } from './logoutSignalService';

type BootPhase = 'idle' | 'phase1' | 'phase2' | 'phase3' | 'complete';

interface BootTiming {
  bootStartTime: number;
  phase1StartTime: number;
  phase1EndTime: number;
  phase2StartTime: number;
  phase2EndTime: number;
  phase3StartTime: number;
  phase3EndTime: number;
}

type Phase2Callback = (smartuserId: string) => Promise<void>;
type Phase3Callback = (smartuserId: string) => Promise<void>;

interface NamedCallback<T extends Phase2Callback | Phase3Callback> {
  name: string;
  callback: T;
}

const CALLBACK_TIMEOUT_MS = 10000;

class BootSchedulerService {
  private currentPhase: BootPhase = 'idle';
  private currentSmartuserId: string | null = null;
  private timing: Partial<BootTiming> = {};
  private phase2Callbacks: NamedCallback<Phase2Callback>[] = [];
  private phase3Callbacks: NamedCallback<Phase3Callback>[] = [];
  private isBootInProgress = false;

  registerPhase2Callback(callback: Phase2Callback, name?: string): void {
    const callbackName = name || `phase2-callback-${this.phase2Callbacks.length}`;
    this.phase2Callbacks.push({ name: callbackName, callback });
    logWithTimestamp(`[BOOT-SCHEDULER] Registered Phase 2 callback: ${callbackName}`);
  }

  registerPhase3Callback(callback: Phase3Callback, name?: string): void {
    const callbackName = name || `phase3-callback-${this.phase3Callbacks.length}`;
    this.phase3Callbacks.push({ name: callbackName, callback });
    logWithTimestamp(`[BOOT-SCHEDULER] Registered Phase 3 callback: ${callbackName}`);
  }

  private async executeCallbackWithTimeout<T>(
    namedCallback: NamedCallback<(smartuserId: string) => Promise<T>>,
    smartuserId: string,
    phase: string
  ): Promise<{ name: string; success: boolean; duration: number; error?: string }> {
    const startTime = Date.now();
    const { name, callback } = namedCallback;

    try {
      await Promise.race([
        callback(smartuserId),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error(`Timeout after ${CALLBACK_TIMEOUT_MS}ms`)), CALLBACK_TIMEOUT_MS)
        ),
      ]);

      const duration = Date.now() - startTime;
      logWithTimestamp(`[BOOT-SCHEDULER] ${phase} callback "${name}" completed in ${duration}ms`);
      return { name, success: true, duration };
    } catch (error) {
      const duration = Date.now() - startTime;
      const errorMsg = error instanceof Error ? error.message : String(error);
      errorWithTimestamp(`[BOOT-SCHEDULER] ${phase} callback "${name}" failed after ${duration}ms:`, errorMsg);
      return { name, success: false, duration, error: errorMsg };
    }
  }

  async startBoot(smartuserId: string): Promise<void> {
    if (this.isBootInProgress && this.currentSmartuserId === smartuserId) {
      logWithTimestamp('[BOOT-SCHEDULER] Boot already in progress for this user, skipping');
      return;
    }

    if (!smartuserId) {
      errorWithTimestamp('[BOOT-SCHEDULER] Cannot start boot: no smartuserId provided');
      return;
    }

    this.isBootInProgress = true;
    this.currentSmartuserId = smartuserId;
    this.timing = { bootStartTime: Date.now() };

    logWithTimestamp('==================================================');
    logWithTimestamp('[BOOT-SCHEDULER] Starting 3-phase boot sequence');
    logWithTimestamp(`[BOOT-SCHEDULER] User: ${smartuserId}`);
    logWithTimestamp('==================================================');

    try {
      await this.executePhase1(smartuserId);
      this.schedulePhase2(smartuserId);
    } catch (error) {
      errorWithTimestamp('[BOOT-SCHEDULER] Critical error during boot:', error);
      this.isBootInProgress = false;
      this.currentPhase = 'idle';
    }
  }

  private async executePhase1(smartuserId: string): Promise<void> {
    this.currentPhase = 'phase1';
    this.timing.phase1StartTime = Date.now();

    logWithTimestamp('[BOOT-SCHEDULER] Phase 1 (Critical) - Loading user preferences...');

    try {
      const preferences = await userPreferencesService.loadAllPreferences(smartuserId);

      this.timing.phase1EndTime = Date.now();
      const duration = this.timing.phase1EndTime - this.timing.phase1StartTime!;

      if (preferences) {
        logWithTimestamp(`[BOOT-SCHEDULER] Phase 1 completed in ${duration}ms`);
        logWithTimestamp(`[BOOT-SCHEDULER]   - Interface language: ${preferences.interfaceLanguage || 'not set'}`);
        logWithTimestamp(`[BOOT-SCHEDULER]   - Subtitle language: ${preferences.subtitleLanguage || 'not set'}`);
        logWithTimestamp(`[BOOT-SCHEDULER]   - Is paying: ${preferences.isPaying}`);
      } else {
        logWithTimestamp(`[BOOT-SCHEDULER] Phase 1 completed in ${duration}ms (no preferences found)`);
      }
    } catch (error) {
      this.timing.phase1EndTime = Date.now();
      const duration = this.timing.phase1EndTime - this.timing.phase1StartTime!;
      errorWithTimestamp(`[BOOT-SCHEDULER] Phase 1 failed after ${duration}ms:`, error);
    }
  }

  private schedulePhase2(smartuserId: string): void {
    logWithTimestamp('[BOOT-SCHEDULER] Scheduling Phase 2 (after UI stable)...');

    if (Platform.OS === 'web') {
      this.scheduleAfterIdleWeb(() => this.executePhase2(smartuserId));
    } else {
      InteractionManager.runAfterInteractions(() => {
        this.executePhase2(smartuserId);
      });
    }
  }

  private async executePhase2(smartuserId: string): Promise<void> {
    if (this.currentSmartuserId !== smartuserId || logoutSignalService.isLoggingOut) {
      logWithTimestamp('[BOOT-SCHEDULER] User changed or logout in progress, aborting Phase 2');
      return;
    }

    this.currentPhase = 'phase2';
    this.timing.phase2StartTime = Date.now();

    const timeSinceBoot = this.timing.phase2StartTime - (this.timing.bootStartTime || 0);
    const callbackCount = this.phase2Callbacks.length;
    logWithTimestamp(`[BOOT-SCHEDULER] Phase 2 (Important) - UI stable after ${timeSinceBoot}ms`);
    logWithTimestamp(`[BOOT-SCHEDULER] Phase 2 executing ${callbackCount} callbacks in PARALLEL`);

    try {
      const results = await Promise.allSettled(
        this.phase2Callbacks.map((namedCallback) =>
          this.executeCallbackWithTimeout(namedCallback, smartuserId, 'Phase 2')
        )
      );

      const successful = results.filter(
        (r) => r.status === 'fulfilled' && r.value.success
      ).length;
      const failed = callbackCount - successful;

      this.timing.phase2EndTime = Date.now();
      const duration = this.timing.phase2EndTime - this.timing.phase2StartTime;
      logWithTimestamp(`[BOOT-SCHEDULER] Phase 2 completed in ${duration}ms (${successful}/${callbackCount} succeeded, ${failed} failed)`);
    } catch (error) {
      this.timing.phase2EndTime = Date.now();
      errorWithTimestamp('[BOOT-SCHEDULER] Phase 2 critical error:', error);
    }

    this.schedulePhase3(smartuserId);
  }

  private schedulePhase3(smartuserId: string): void {
    logWithTimestamp('[BOOT-SCHEDULER] Scheduling Phase 3 (background)...');

    const delay = 100;

    if (Platform.OS === 'web') {
      this.scheduleAfterIdleWeb(() => {
        setTimeout(() => this.executePhase3(smartuserId), delay);
      });
    } else {
      InteractionManager.runAfterInteractions(() => {
        setTimeout(() => this.executePhase3(smartuserId), delay);
      });
    }
  }

  private async executePhase3(smartuserId: string): Promise<void> {
    if (this.currentSmartuserId !== smartuserId || logoutSignalService.isLoggingOut) {
      logWithTimestamp('[BOOT-SCHEDULER] User changed or logout in progress, aborting Phase 3');
      return;
    }

    this.currentPhase = 'phase3';
    this.timing.phase3StartTime = Date.now();

    const timeSinceBoot = this.timing.phase3StartTime - (this.timing.bootStartTime || 0);
    const callbackCount = this.phase3Callbacks.length;
    logWithTimestamp(`[BOOT-SCHEDULER] Phase 3 (Deferred) - Starting after ${timeSinceBoot}ms`);
    logWithTimestamp(`[BOOT-SCHEDULER] Phase 3 executing ${callbackCount} callbacks in PARALLEL`);

    try {
      const results = await Promise.allSettled(
        this.phase3Callbacks.map((namedCallback) =>
          this.executeCallbackWithTimeout(namedCallback, smartuserId, 'Phase 3')
        )
      );

      const successful = results.filter(
        (r) => r.status === 'fulfilled' && r.value.success
      ).length;
      const failed = callbackCount - successful;

      this.timing.phase3EndTime = Date.now();
      const duration = this.timing.phase3EndTime - this.timing.phase3StartTime;
      logWithTimestamp(`[BOOT-SCHEDULER] Phase 3 completed in ${duration}ms (${successful}/${callbackCount} succeeded, ${failed} failed)`);
    } catch (error) {
      this.timing.phase3EndTime = Date.now();
      errorWithTimestamp('[BOOT-SCHEDULER] Phase 3 critical error:', error);
    }

    this.completeBoot();
  }

  private completeBoot(): void {
    this.currentPhase = 'complete';
    this.isBootInProgress = false;

    const totalDuration = Date.now() - (this.timing.bootStartTime || 0);

    logWithTimestamp('==================================================');
    logWithTimestamp('[BOOT-SCHEDULER] Boot sequence complete');
    logWithTimestamp(`[BOOT-SCHEDULER] Total duration: ${totalDuration}ms`);
    logWithTimestamp('[BOOT-SCHEDULER] Timing breakdown:');
    if (this.timing.phase1StartTime && this.timing.phase1EndTime) {
      logWithTimestamp(`[BOOT-SCHEDULER]   Phase 1: ${this.timing.phase1EndTime - this.timing.phase1StartTime}ms`);
    }
    if (this.timing.phase2StartTime && this.timing.phase2EndTime) {
      const waitTime = this.timing.phase2StartTime - (this.timing.phase1EndTime || 0);
      logWithTimestamp(`[BOOT-SCHEDULER]   Wait for UI: ${waitTime}ms`);
      logWithTimestamp(`[BOOT-SCHEDULER]   Phase 2: ${this.timing.phase2EndTime - this.timing.phase2StartTime}ms`);
    }
    if (this.timing.phase3StartTime && this.timing.phase3EndTime) {
      logWithTimestamp(`[BOOT-SCHEDULER]   Phase 3: ${this.timing.phase3EndTime - this.timing.phase3StartTime}ms`);
    }
    logWithTimestamp('==================================================');
  }

  private scheduleAfterIdleWeb(callback: () => void): void {
    if (typeof requestIdleCallback !== 'undefined') {
      requestIdleCallback(() => callback(), { timeout: 2000 });
    } else {
      setTimeout(callback, 0);
    }
  }

  getCurrentPhase(): BootPhase {
    return this.currentPhase;
  }

  isPhaseComplete(phase: BootPhase): boolean {
    const phaseOrder: BootPhase[] = ['idle', 'phase1', 'phase2', 'phase3', 'complete'];
    const currentIndex = phaseOrder.indexOf(this.currentPhase);
    const targetIndex = phaseOrder.indexOf(phase);
    return currentIndex > targetIndex;
  }

  reset(): void {
    this.currentPhase = 'idle';
    this.currentSmartuserId = null;
    this.timing = {};
    this.isBootInProgress = false;
    logWithTimestamp('[BOOT-SCHEDULER] Reset');
  }

  getTiming(): Partial<BootTiming> {
    return { ...this.timing };
  }
}

export const bootSchedulerService = new BootSchedulerService();
